import { Box, Grid, makeStyles, Paper, TableCell, Toolbar } from '@material-ui/core';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Colors, Button, Heading, Icon, NavButton, Paragraph, TableHeader } from 'styleguide';
import { Domain } from '../../constants/classifierConstants';
import { fetchGet, fetchRowDataWithParam, toggleRow } from '../../stores/register/registerReport';
import ChartUtils from '../../utils/ChartUtils';
import DateUtils from '../../utils/DateUtils';
import PdfUtils from '../../utils/PdfUtils';
import TableUtils from '../../utils/TableUtils';
import { CenteredLoadingIndicator } from '../form/LoadingIndicator';
import SimplePageableTable from '../table/SimplePageableTable';

const useStyles = makeStyles((theme) => ({
  flex: {
    flexGrow: 1
  },
  header: {
    paddingLeft: 16
  },
  toolbar: {
    padding: 0,
  },
  exportChart: {
    right: 24,
    top: 24,
    float: 'right',
    zIndex: 2
  },
  iconCell: {
    verticalAlign: 'middle',
    marginRight: 8
  },
}));

export default function ShieldReport() {
  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const reportName = 'shield';
  const rows = useSelector(state => state.registerReport.rows[reportName]);
  const isLoading = useSelector(state => state.registerReport.isLoading);
  const compact = useSelector(state => state.global.mobileView);

  const title = t(`report.TV.shield`);
  const subTitle = t('report.query');

  const columns = useMemo(() => {
    let columns = [
      {
        field: 'location', headerName: t('report.table.location'), notSortable: true,
        renderCell: (column, cell) => <Grid container>
          {!!cell.location && <Icon icon={cell.open ? 'minus' : 'plus'} className={classes.iconCell} />}
          <Paragraph fontSize='14B'>
            {!!cell.location ? cell.location : t('report.table.total')}
          </Paragraph>
        </Grid>
      },
      {
        field: 'shields', headerName: t(`report.table.shieldCount`), notSortable: true, renderCell: (column, cell) => {
          const value = `${TableUtils.formatNumber(cell.shields) || 0} ${t('report.table.unitPieces')}`;
          if (!cell.location) {
            return <Paragraph fontSize='14B'>{value}</Paragraph>;
          }
          return value;
        }
      },
      {
        field: 'lamps', headerName: t(`report.table.lampCount`), notSortable: true, renderCell: (column, cell) => {
          const value = `${TableUtils.formatNumber(cell.lamps) || 0} ${t('report.table.unitPieces')}`;
          if (!cell.location) {
            return <Paragraph fontSize='14B'>{value}</Paragraph>;
          }
          return value;
        }
      },
    ];
    return columns;
  }, [t, classes]);

  const shouldFetch = !rows;
  useEffect(() => shouldFetch && dispatch(fetchGet(Domain.streetlight, reportName)), [dispatch, shouldFetch]);

  const handleClose = () => navigate(-1);

  const header = <TableHeader>{t('report.table.shieldLampTitle')}</TableHeader>;

  if (!rows || isLoading) {
    return <CenteredLoadingIndicator />;
  }

  const handleChartExport = () => {
    PdfUtils.saveImage('chart', `${subTitle} ${title} ${DateUtils.nowISODate()}`);
  };

  const handleRowSelection = (row) => {
    if (row.location) {
      if (!row.data) {
        dispatch(fetchRowDataWithParam(Domain.streetlight, reportName, row, `/location?param=${row.location}`));
      }
      dispatch(toggleRow(row, !row.open, reportName));
    }
  };

  const handleRenderCollapsibleRow = (row) => {
    if (row.open && row.data) {
      return <>
        <TableCell>{row.data.map((d, i) => <div key={i}>{d.title}</div>)}</TableCell>
        <TableCell>{row.data.map((d, i) => <div key={i}>{!!d.power ? `${TableUtils.formatNumber(d.power)} ${t('report.table.unitA')}` : '-'}</div>)}</TableCell>
        <TableCell>{row.data.map((d, i) => <div key={i}>{`${TableUtils.formatNumber(d.count || 0)} ${t('report.table.unitPieces')}`}</div>)}</TableCell>
      </>;
    }
  };

  return <div>
    <Toolbar className={classes.toolbar}>
      <NavButton onClick={handleClose} action='back' color='secondary'></NavButton>
      <span className={classes.header}>
        <Heading level={compact ? '4' : '3'}>{title}</Heading>
        <Paragraph fontSize={compact ? '14' : '14B'}>{subTitle}</Paragraph>
      </span>
    </Toolbar>

    <Box py={4}>
      <SimplePageableTable
        id="report-table"
        rows={rows}
        columns={columns}
        header={header}
        pageSize={200}
        disableLoading
        loading={isLoading || !rows}
        loadingRowsCount={1}
        onRenderCollapsibleRow={handleRenderCollapsibleRow}
        onRowSelected={handleRowSelection}
      />
    </Box>

    {!compact && rows && <Paper>
      <Button size="extra-small" color="tertiary" onClick={handleChartExport}
        disabled={isLoading || !rows?.length} className={classes.exportChart}>
        <Icon icon="files" color={Colors.sinineVaal} /> {t('report.exportPng')}
      </Button>
      <ResponsiveContainer width="100%" height={600} id="chart">
        <BarChart
          width={500}
          height={600}
          data={rows.filter(r => !!r.location)}
          margin={{
            top: 50,
            right: 50,
            left: 30,
            bottom: 100,
          }}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey="location" />
          <YAxis yAxisId="left" orientation="left" unit={t('report.table.unitPieces')} />
          <YAxis yAxisId="right" orientation="right" unit={t('report.table.unitPieces')} />
          <Tooltip />
          <Legend iconSize={16} iconType="square" formatter={ChartUtils.renderLegendText} wrapperStyle={{ position: 'relative' }} />
          <Bar yAxisId="left" dataKey="lamps" name={t('report.table.lampCount')} fill={ChartUtils.getGraphColor(0)} radius={4}
            label={{ position: 'top' }} />
          <Bar yAxisId="right" dataKey="shields" name={t(`report.table.shieldCount`)} fill={ChartUtils.getGraphColor(1)} radius={4}
            label={{ position: 'top' }} />
        </BarChart>
      </ResponsiveContainer>
    </Paper>}
  </div>;
}